'use strict';

const ENDPOINT = 'https://z7fslpjt3d.execute-api.ap-southeast-1.amazonaws.com';
const Commerce = require('./commerce.js');
const UUID = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

const error = (code, status = 503) => Object.assign(new Error(code), {
  code,
  status
});

const need = (condition, code, status) => {
  if (!condition) throw error(code, status);
};

function capabilities(value) {
  const legacy=value&&(value.mode==='CLOSED_READS_V1'&&value.openingEnabled===false||value.mode==='OPENING_V1'&&value.openingEnabled===true)&&value.salesEnabled===false;
  const commerce=value&&value.mode==='COMMERCE_V1'&&value.salesEnabled===true&&typeof value.openingEnabled==='boolean';
  need(value&&value.feature==='OP_CARD'&&value.available===true&&(legacy||commerce)&&value.grantsEnabled===false,'INVALID_CAPABILITIES');
  return Object.freeze({feature:'OP_CARD',available:true,mode:value.mode,salesEnabled:value.salesEnabled,openingEnabled:value.openingEnabled,grantsEnabled:false});
}

function route(method, path, body, openingEnabled = false, salesEnabled = false) {
  need(method === 'GET' || method === 'POST' && (openingEnabled === true || salesEnabled === true), 'CARD_WRITES_DISABLED', 403);
  need(typeof path === 'string' && path.length <= 4096 && path.startsWith('/op-cards/'), 'INVALID_OP_ROUTE', 400);
  const url = new URL(path, ENDPOINT);
  need(url.origin === ENDPOINT && !url.hash && !url.username && !url.password, 'INVALID_OP_ROUTE', 400);

  if (method === 'POST') {
    need(!url.search,'INVALID_OP_QUERY',400);
    if(path==='/op-cards/checkouts'){
      need(salesEnabled===true,'CARD_WRITES_DISABLED',403);
      need(body&&typeof body==='object'&&!Array.isArray(body)&&Object.keys(body).sort().join(',')===(body.activityId===undefined?'idempotencyKey,offerId,paymentMethod,skuId':'activityId,idempotencyKey,offerId,paymentMethod,skuId'),'INVALID_CHECKOUT_REQUEST',400);
      Commerce.selection({skuId:body.skuId,...(body.activityId===undefined?{}:{activityId:body.activityId})});
      need(Object.prototype.hasOwnProperty.call(Commerce.METHODS,body.paymentMethod)&&/^[A-Za-z0-9][A-Za-z0-9._:-]{0,179}$/.test(body.offerId||'')&&/^[A-Za-z0-9][A-Za-z0-9._:-]{0,179}$/.test(body.idempotencyKey||''),'INVALID_CHECKOUT_REQUEST',400);
      return path;
    }
    if(/^\/op-cards\/checkouts\/[A-Za-z0-9][A-Za-z0-9._:-]{0,179}\/resume$/.test(path)){
      need(salesEnabled===true,'CARD_WRITES_DISABLED',403);need(body&&typeof body==='object'&&!Array.isArray(body)&&Object.keys(body).length===0,'INVALID_CHECKOUT_REQUEST',400);return path;
    }
    need(openingEnabled===true&&/^\/op-cards\/packs\/[A-Za-z0-9][A-Za-z0-9._:-]{0,179}\/(reveal|open)$/.test(path),'CARD_WRITES_DISABLED',403);
    need(body&&typeof body==='object'&&!Array.isArray(body)&&Object.keys(body).join(',')==='requestId'&&typeof body.requestId==='string'&&/^[A-Za-z0-9][A-Za-z0-9._:-]{0,179}$/.test(body.requestId),'INVALID_OPENING_REQUEST',400);
    return path;
  }

  need(body === undefined, 'CARD_WRITES_DISABLED', 403);
  const allowed = {
    '/op-cards/capabilities': [],
    '/op-cards/catalogue': ['catalogueHash'],
    '/op-cards/packs': ['cursor'],
    '/op-cards/collection': [],
    '/op-cards/activities': [],
    '/op-cards/checkout-offers': ['skuId','activityId']
  };
  let keys = allowed[url.pathname];
  if(/^\/op-cards\/checkouts\/[A-Za-z0-9][A-Za-z0-9._:-]{0,179}$/.test(url.pathname))keys=[];
  if(url.pathname==='/op-cards/checkout-offers'){need(salesEnabled===true,'CARD_WRITES_DISABLED',403);Commerce.selection({skuId:url.searchParams.get('skuId'),...(url.searchParams.has('activityId')?{activityId:url.searchParams.get('activityId')}:{})});}
  if (!keys && /^\/op-cards\/packs\/[A-Za-z0-9][A-Za-z0-9._:-]{0,179}$/.test(url.pathname)) keys = [];
  if (!keys && /^\/op-cards\/activities\/[A-Za-z0-9][A-Za-z0-9._:-]{0,179}$/.test(url.pathname)) keys = [];
  need(keys !== undefined, 'INVALID_OP_ROUTE', 400);

  for (const [key, value] of url.searchParams.entries()) {
    need(keys.includes(key) && url.searchParams.getAll(key).length === 1, 'INVALID_OP_QUERY', 400);
    if (key === 'catalogueHash') need(/^[a-f0-9]{64}$/.test(value), 'INVALID_OP_QUERY', 400);
    if (key === 'cursor') need(value.length > 0 && value.length <= 1024, 'INVALID_OP_QUERY', 400);
  }

  return path;
}

function createReadClient({
  getSession,
  getIdentity,
  send,
  now = Date.now,
  onUnavailable = () => {},
  setTimer = setTimeout,
  clearTimer = clearTimeout
}) {
  need(typeof getSession === 'function' && typeof getIdentity === 'function' && typeof send === 'function', 'AUTHENTICATED_SDK_REQUIRED');
  let disposed = false,
      permitted = false,
      openingPermitted = false,
      salesPermitted = false,
      owner = null,
      active = null,
      discovery = null,
      unavailableNotified = false;
  const pending = new Map(),
        queue = [],
        delays = new Set();

  const alive = () => need(!disposed, 'SESSION_CHANGED', 401);

  const identity = () => {
    alive();
    const value = getIdentity();
    need(value && UUID.test(value.userId || '') && UUID.test(value.gameId || ''), 'UNAUTHORIZED', 401);
    need(owner === null || owner === value.userId + ':' + value.gameId, 'SESSION_CHANGED', 401);
    return {
      userId: value.userId,
      gameId: value.gameId
    };
  };

  function settle(job, failure, value) {
    if (job.settled) return;
    job.settled = true;
    if (pending.get(job.key) === job) pending.delete(job.key);
    if (failure) job.reject(failure);else job.resolve(value);
  }

  function cancelWaiting(failure) {
    for (const job of queue.splice(0)) settle(job, failure);

    for (const delay of delays) {
      clearTimer(delay.timer);
      delay.reject(failure);
    }

    delays.clear();
  }

  function normalized(failure, origin = null) {
    var _failure$response, _failure$response2, _failure$response2$da;

    const status = (failure === null || failure === void 0 ? void 0 : (_failure$response = failure.response) === null || _failure$response === void 0 ? void 0 : _failure$response.status) || (failure === null || failure === void 0 ? void 0 : failure.status) || 503;
    const code = (failure === null || failure === void 0 ? void 0 : (_failure$response2 = failure.response) === null || _failure$response2 === void 0 ? void 0 : (_failure$response2$da = _failure$response2.data) === null || _failure$response2$da === void 0 ? void 0 : _failure$response2$da.error) || (failure === null || failure === void 0 ? void 0 : failure.code);
    const detail=failure&&failure.response&&failure.response.data;
    const checkoutError=status===409&&['OFFER_EXPIRED','OFFER_CHANGED','CHECKOUT_ALREADY_PENDING'].includes(code);
    const result = error(code === 'SESSION_CHANGED' ? code : status === 403 ? 'FEATURE_NOT_AVAILABLE' : status === 401 ? 'UNAUTHORIZED' : checkoutError?code:'OP_CARD_READ_UNAVAILABLE', status);
    if(checkoutError&&detail&&detail.checkoutCreated===false)result.checkoutCreated=false;
    if(checkoutError&&detail&&typeof detail.checkoutId==='string'&&/^[A-Za-z0-9][A-Za-z0-9._:-]{0,179}$/.test(detail.checkoutId))result.checkoutId=detail.checkoutId;

    if (status === 401 || status === 403) {
      permitted = false;
      openingPermitted = false;
      salesPermitted = false;
      cancelWaiting(result); // Provider revocation synchronously disposes this client. Preserve the
      // initiating transport's actual auth rejection before that cancellation.

      if (origin && active === origin) settle(origin, result);

      if (!disposed && !unavailableNotified) {
        unavailableNotified = true;

        try {
          onUnavailable(status === 403 ? 'FEATURE_NOT_AVAILABLE' : 'UNAUTHORIZED');
        } catch (_) {}
      }
    }

    return result;
  }

  function transient(failure) {
    var _failure$response3;

    if (['INVALID_RESPONSE', 'INVALID_CAPABILITIES', 'SESSION_CHANGED', 'UNAUTHORIZED', 'FEATURE_NOT_AVAILABLE', 'CARD_WRITES_DISABLED'].includes((failure === null || failure === void 0 ? void 0 : failure.code) || (failure === null || failure === void 0 ? void 0 : failure.message))) return false;
    const status = (failure === null || failure === void 0 ? void 0 : (_failure$response3 = failure.response) === null || _failure$response3 === void 0 ? void 0 : _failure$response3.status) || (failure === null || failure === void 0 ? void 0 : failure.status);
    if (status !== undefined) return [429, 502, 503, 504].includes(status);
    return !(failure === null || failure === void 0 ? void 0 : failure.response) && (['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK', 'ECONNRESET', 'ENOTFOUND', 'EAI_AGAIN'].includes(failure === null || failure === void 0 ? void 0 : failure.code) || (failure === null || failure === void 0 ? void 0 : failure.message) === 'Network Error');
  }

  function pause(ms) {
    alive();
    return new Promise((resolve, reject) => {
      const delay = {
        reject,
        timer: null
      };
      delays.add(delay);
      delay.timer = setTimer(() => {
        delays.delete(delay);
        resolve();
      }, ms);
    });
  }

  async function attempt(job) {
    const {
      path,
      method,
      body
    } = job;
    alive();
    if (path !== '/op-cards/capabilities') need(permitted, 'FEATURE_NOT_AVAILABLE', 403);
    if (method === 'POST') route(method,path,body,openingPermitted,salesPermitted);
    const session = await getSession(),
          who = identity();
    need(session && UUID.test(session.token || '') && Number.isSafeInteger(session.expiresAt) && session.expiresAt > now() + 1000, 'UNAUTHORIZED', 401);
    owner = who.userId + ':' + who.gameId;
    const value = await send({
      method,
      path,
      token: session.token,
      ...(body === undefined ? {} : {
        body
      })
    });
    identity();
    return {
      value,
      identity: who,
      expiresAt: session.expiresAt
    };
  }

  async function execute(job) {
    for (let retry = 0; retry < 3; retry++) {
      try {
        return await attempt(job);
      } catch (failure) {
        if (disposed) throw error('SESSION_CHANGED', 401);

        if (job.method === 'GET' && retry < 2 && transient(failure)) {
          try {
            alive();
            if (owner !== null) identity();
            await pause(retry === 0 ? 250 : 750);
            alive();
            if (owner !== null) identity();
          } catch (cancelled) {
            throw normalized(cancelled, job);
          }

          continue;
        }

        throw normalized(failure, job);
      }
    }
  }

  function pump() {
    if (disposed || active || !queue.length) return;
    const job = queue.shift();
    active = job;
    Promise.resolve().then(() => execute(job)).then(value => settle(job, null, value), failure => settle(job, failure)).then(() => {
      active = null;
      pump();
    });
  }

  function call(path, method = 'GET', body) {
    try {
      alive();
      if (owner !== null) identity();
    } catch (failure) {
      return Promise.reject(normalized(failure));
    }

    const key = method + ':' + path + (body === undefined ? '' : ':' + JSON.stringify(body));
    if (pending.has(key)) return pending.get(key).promise; // One active transport per authenticated client; pending work is bounded and
    // shared only while in flight. Never cache a previous account's response.

    if (pending.size >= 16) return Promise.reject(error('OP_CARD_READ_QUEUE_FULL', 503));
    const job = {
      key,
      path,
      method,
      body,
      settled: false
    };
    job.promise = new Promise((resolve, reject) => {
      job.resolve = resolve;
      job.reject = reject;
    });
    pending.set(key, job);
    queue.push(job);
    pump();
    return job.promise;
  }

  function discover() {
    if (discovery) return discovery;
    permitted = false;
    openingPermitted = false;
    salesPermitted = false;
    unavailableNotified = false;

    const job = (async () => {
      const result = await call('/op-cards/capabilities');
      const capability = capabilities(result.value);
      identity();
      permitted = true;
      openingPermitted = capability.openingEnabled === true;
      salesPermitted = capability.salesEnabled === true;
      return { ...result,
        capabilities: capability,
        value: undefined
      };
    })();

    discovery = job;

    const clear = () => {
      if (discovery === job) discovery = null;
    };

    job.then(clear, clear);
    return job;
  }

  async function request({
    method,
    path,
    body
  }) {
    route(method, path, body, openingPermitted, salesPermitted);
    need(permitted, 'FEATURE_NOT_AVAILABLE', 403);
    const retainedBody = body === undefined ? undefined : Object.freeze({...body});
    const result = await call(path, method, retainedBody);
    alive();
    need(permitted, 'FEATURE_NOT_AVAILABLE', 403);
    return result.value;
  }

  return Object.freeze({
    discover,
    request,

    dispose() {
      disposed = true;
      permitted = false;
      openingPermitted = false;
      salesPermitted = false;
      owner = null;
      const failure = error('SESSION_CHANGED', 401);
      if (active) settle(active, failure);
      cancelWaiting(failure);
    },

    endpoint: ENDPOINT
  });
}

module.exports = {
  ENDPOINT,
  capabilities,
  route,
  createReadClient
};
